// src/pages/AutomationPage.jsx
import {
  ArrowRight,
  Bot,
  Calendar,
  CheckCircle2,
  Mail,
  MessageSquare,
  Moon,
  Phone,
  Sparkles,
  Sun,
  Zap
} from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import AnimatedBackground from '../components/AnimatedBackground';
import Footer from '../components/Footer';
import RequestModal from '../components/RequestModal';
import SEO from '../components/SEO';
import AdBanner from '../components/AdBanner';

const AutomationPage = ({ darkMode, setDarkMode, changeLanguage }) => {
  const { t } = useTranslation();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState({ name: '', price: '' });

  const handlePlanSelect = (planName, planPrice) => {
    setSelectedPlan({ name: planName, price: planPrice });
    setIsModalOpen(true);
  };

  return (
    <>
      <SEO
        title="AI Automation & MaryGuardian | COMBI.kz"
        description="Automate business processes, meetings and planning. Discover MaryGuardian - your local AI assistant."
        path="/automation"
      />

      <AnimatedBackground darkMode={darkMode} />
      <div className="min-h-screen relative z-10 text-fg transition-colors duration-500">

        {/* Header */}
        <header className="fixed top-0 left-0 right-0 w-full z-50 bg-bg/80 backdrop-blur-md shadow-sm border-b border-fg/10">
          <div className="container mx-auto px-6 py-4 flex justify-between items-center">
            <Link to="/" className="hidden md:flex text-2xl font-bold bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent hover:opacity-80 transition-opacity cursor-pointer group items-center gap-2">
              COMBI<span className="text-fg/50">.kz</span>
              <Sparkles size={16} className="opacity-0 group-hover:opacity-100 transition-opacity text-primary" />
            </Link>

            <div className="md:hidden"></div>

            <div className="flex gap-2 md:gap-4 items-center">
              <Link to="/" className="px-2 md:px-4 py-1 md:py-2 bg-gradient-to-r from-primary to-secondary text-white rounded-lg hover:shadow-lg transition-all hover:scale-105 text-xs md:text-sm font-medium whitespace-nowrap">
                {t('automation')}
              </Link>

              <div className="flex gap-1 md:gap-2 items-center">
                <button onClick={() => changeLanguage('ru')} className="p-1 md:p-2 rounded-lg hover:bg-fg/10 transition">
                  <span className="fi fi-ru text-sm md:text-base"></span>
                </button>
                <button onClick={() => changeLanguage('en')} className="p-1 md:p-2 rounded-lg hover:bg-fg/10 transition">
                  <span className="fi fi-gb text-sm md:text-base"></span>
                </button>
                <button onClick={() => changeLanguage('kk')} className="p-1 md:p-2 rounded-lg hover:bg-fg/10 transition">
                  <span className="fi fi-kz text-sm md:text-base"></span>
                </button>
                <button onClick={() => setDarkMode(!darkMode)} className="p-1 md:p-2 rounded-lg bg-fg/10 backdrop-blur-sm hover:bg-fg/20 transition">
                  {darkMode ? <Sun size={16} className="md:w-5 md:h-5 text-accent" /> : <Moon size={16} className="md:w-5 md:h-5 text-fg/70" />}
                </button>
              </div>
            </div>
          </div>
        </header>

        <main className="pt-20 pb-12">
          {/* Main Split Section */}
          <section className="container mx-auto px-6 py-12 lg:py-20">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">

              {/* Left Side: Automation Services */}
              <div className="space-y-8">
                <div className="inline-flex items-center gap-2 bg-primary/10 backdrop-blur-sm text-primary px-4 py-2 rounded-full border border-primary/20">
                  <Zap size={16} />
                  <span className="text-sm font-medium">{t('automationBadge')}</span>
                </div>

                <h1 className="text-4xl md:text-6xl font-bold leading-tight">
                  {t('automationHeroTitle1')}
                  <br />
                  <span className="bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
                    {t('automationHeroTitle2')}
                  </span>
                </h1>

                <p className="text-xl text-fg/60 max-w-lg">
                  {t('automationHeroDesc')}
                </p>

                <div className="space-y-4">
                  <div className="flex items-start gap-4 p-4 rounded-xl backdrop-blur-md bg-bg/60 dark:bg-fg/10 border border-fg/10 hover:border-primary/30 transition-colors">
                    <Bot className="w-6 h-6 text-primary mt-1" />
                    <div>
                      <h3 className="font-semibold text-lg">{t('solution1Title')}</h3>
                      <p className="text-fg/60 text-sm">{t('solution1Desc')}</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4 p-4 rounded-xl backdrop-blur-md bg-bg/60 dark:bg-fg/10 border border-fg/10 hover:border-primary/30 transition-colors">
                    <Calendar className="w-6 h-6 text-secondary mt-1" />
                    <div>
                      <h3 className="font-semibold text-lg">{t('solution2Title')}</h3>
                      <p className="text-fg/60 text-sm">{t('solution2Desc')}</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4 p-4 rounded-xl backdrop-blur-md bg-bg/60 dark:bg-fg/10 border border-fg/10 hover:border-primary/30 transition-colors">
                    <MessageSquare className="w-6 h-6 text-accent mt-1" />
                    <div>
                      <h3 className="font-semibold text-lg">{t('solution3Title')}</h3>
                      <p className="text-fg/60 text-sm">{t('solution3Desc')}</p>
                    </div>
                  </div>
                </div>

                <div className="pt-4">
                  <button
                    onClick={() => handlePlanSelect(t('guardianCtaPrimary'), '0')}
                    className="group flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-primary to-secondary text-white rounded-xl hover:shadow-xl transition-all hover:scale-[1.02] text-lg font-semibold"
                  >
                    {t('clarifyButton')}
                    <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
                  </button>
                  <p className="mt-3 text-sm text-fg/40">{t('guardianAlphaNote')}</p>
                </div>
              </div>

              {/* Right Side: MaryGuardian Product Card */}
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-r from-primary/20 to-secondary/20 blur-3xl opacity-30 rounded-full"></div>

                <div className="relative backdrop-blur-md bg-bg/60 dark:bg-fg/10 border border-fg/10 rounded-3xl p-8 shadow-2xl overflow-hidden group hover:border-primary/30 transition-all duration-500">
                  <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity">
                    <Sparkles size={120} />
                  </div>

                  <div className="relative z-10">
                    <div className="flex items-center gap-3 mb-6">
                      <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary to-secondary flex items-center justify-center text-white shadow-lg">
                        <Bot size={24} />
                      </div>
                      <div>
                        <h3 className="text-2xl font-bold">MaryGuardian</h3>
                        <span className="text-xs font-mono text-primary bg-primary/10 px-2 py-0.5 rounded">{t('guardianBadge')}</span>
                      </div>
                    </div>

                    <p className="text-fg/70 mb-6 leading-relaxed">
                      {t('guardianHeroDesc')}
                    </p>

                    <ul className="space-y-3 mb-8">
                      <li className="flex items-center gap-2 text-sm text-fg/80">
                        <CheckCircle2 size={16} className="text-primary" />
                        <span>{t('guardianFeat1Title')}</span>
                      </li>
                      <li className="flex items-center gap-2 text-sm text-fg/80">
                        <CheckCircle2 size={16} className="text-primary" />
                        <span>{t('guardianFeat3Title')}</span>
                      </li>
                      <li className="flex items-center gap-2 text-sm text-fg/80">
                        <CheckCircle2 size={16} className="text-primary" />
                        <span>{t('guardianFeat4Title')}</span>
                      </li>
                    </ul>

                    <Link
                      to="/mary-guardian"
                      className="block w-full py-3 text-center bg-fg/10 hover:bg-fg/20 text-fg rounded-xl font-semibold transition-all border border-fg/10 hover:border-fg/20"
                    >
                      {t('guardianCtaPrimary')}
                    </Link>
                  </div>
                </div>
              </div>

            </div>
          </section>

          {/* Simple CTA Section */}
          <section className="container mx-auto px-6 py-12">
            <div className="bg-bg/60 dark:bg-fg/10 backdrop-blur-md rounded-2xl p-8 md:p-12 text-center border border-primary/10 shadow-lg">
              <h2 className="text-3xl font-bold mb-4">{t('ctaTitle')}</h2>
              <p className="text-lg mb-8 text-fg/70 max-w-2xl mx-auto">
                {t('ctaSubtitle')}
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <a href="mailto:khamit@combi.kz" className="flex items-center gap-2 px-6 py-3 bg-primary backdrop-blur-sm text-fg rounded-xl hover:shadow-lg transition hover:scale-105 font-semibold border border-fg/10">
                  <Mail size={20} /> {t('email')}
                </a>
                <a href="tel:+77712292371" className="flex items-center gap-2 px-6 py-3 bg-primary/10 rounded-xl hover:bg-fg/20 transition hover:scale-105 backdrop-blur-sm border border-fg/10">
                  <Phone size={20} /> {t('phone')}
                </a>
              </div>
              <p className="mt-6 text-sm text-fg/50">{t('ctaFooter')}</p>
            </div>
          </section>

          {/* ✅ ВСТАВЬТЕ РЕКЛАМУ ЗДЕСЬ: перед футером, 
          внутри main, где она не перекроет контент */}
          <AdBanner />

        </main>

        <RequestModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          planName={selectedPlan.name}
          planPrice={selectedPlan.price}
        />
      </div>
    </>
  );
};

export default AutomationPage;